// src/router/index.js

import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
// import store from '@/state/store';
import apiClient from '@/services/api';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication
// Before each route evaluates...

router.beforeEach(async (routeTo, routeFrom, next) => {
  console.log('Router guard - navigating from:', routeFrom.path, 'to:', routeTo.path);
  
  // Define public pages that don't require authentication
  const publicPages = ['/login', '/register', '/forgot-password'];
  const isPublicPage = publicPages.includes(routeTo.path);
  
  // If it's a public page, allow access
  if (isPublicPage) {
    console.log('Router guard - public page, allowing access');
    return next();
  }
  
  // For protected pages, check authentication
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  const token = localStorage.getItem('jwt');
  
  console.log('Router guard - authRequired:', authRequired, 'token exists:', !!token);
  
  // If auth is required but no token, redirect to login
  if (authRequired && !token) {
    console.log('Router guard - no token, redirecting to login');
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }
  
  // If we have a token, verify it with the server
  if (authRequired && token) {
    try {
      console.log('Router guard - verifying token with server');
      const response = await apiClient.get("/auth/profile");
      // Update localStorage with fresh user data
      localStorage.setItem('userdata', JSON.stringify(response.data));
      localStorage.setItem('userid', response.data.id || response.data.user_id);
      localStorage.setItem('user', JSON.stringify(response.data));
      console.log('Router guard - token valid, proceeding to route');
      next();
    } catch (error) {
      console.log('Router guard - token invalid, redirecting to login', error);
      // Token is invalid, clear localStorage and redirect to login
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
      localStorage.removeItem('userdata');
      localStorage.removeItem('userid');
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
  } else {
    // No auth required, proceed
    console.log('Router guard - no auth required, proceeding');
    next();
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
